import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/apiClient';
import IntentModal from './IntentModal';

const IntentsManager = ({ projectId, project, canManage = true, hasReadOnlyAccess = false }) => {
  const { isAuthenticated } = useAuth();
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [intentClassificationEnabled, setIntentClassificationEnabled] = useState(true);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  
  // Sync to production state
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [intentToSync, setIntentToSync] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Fetch intents
  const fetchIntents = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/flow-builder/projects/${projectId}/intents`);
      
      // Ensure we always set an array
      const intentsData = response.data?.data?.intents || response.data?.intents || response.data || [];
      setIntents(Array.isArray(intentsData) ? intentsData : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch intents');
      console.error('Error fetching intents:', err);
      // Ensure intents is always an array even on error
      setIntents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId && isAuthenticated) {
      fetchIntents();
    }
  }, [projectId, isAuthenticated]);

  // Initialize intent classification setting from project data
  useEffect(() => {
    if (project?.intentClassification) {
      setIntentClassificationEnabled(project.intentClassification.enabled !== false);
    }
  }, [project]);

  // Handle create intent
  const handleCreateIntent = () => {
    setSelectedIntent(null);
    setShowModal(true);
  };

  // Handle edit intent
  const handleEditIntent = (intent) => {
    setSelectedIntent(intent);
    setShowModal(true);
  };

  // Handle delete intent
  const handleDeleteIntent = async (intentId) => {
    if (!window.confirm('Are you sure you want to delete this intent?')) {
      return;
    }

    try {
      await apiClient.delete(`/flow-builder/projects/${projectId}/intents/${intentId}`);
      await fetchIntents(); // Refresh the list
    } catch (err) {
      console.error('Error deleting intent:', err);
      alert('Failed to delete intent');
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
    setSelectedIntent(null);
  };

  // Handle intent save
  const handleIntentSave = async () => {
    await fetchIntents(); // Refresh the list
    handleModalClose();
  };

  // Handle intent classification toggle
  const handleIntentClassificationToggle = async (enabled) => {
    setUpdatingSettings(true);
    setError(null);
    
    try {
      console.log('Updating intent classification setting:', enabled);
      
      const response = await apiClient.patch(`/projects/${projectId}/settings`, {
        intentClassification: {
          ...project?.intentClassification,
          enabled: enabled
        }
      });
      
      console.log('Intent classification update response:', response);
      setIntentClassificationEnabled(enabled);
      
      // Show success message briefly
      const successMessage = enabled 
        ? 'LLM-based intent classification enabled' 
        : 'Switched to rule-based intent detection';
      
      // You could add a toast notification here if you have one
      console.log(successMessage);
      
    } catch (err) {
      console.error('Error updating intent classification:', err);
      setError(err.response?.data?.message || 'Failed to update intent classification setting');
      // Revert the toggle on error
      setIntentClassificationEnabled(!enabled);
    } finally {
      setUpdatingSettings(false);
    }
  };

  // Sync to production functionality
  const shouldShowSyncButton = () => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    return environment === 'local' || environment === 'staging';
  };

  const handleSyncToProduction = (intent) => {
    setIntentToSync(intent);
    setShowSyncModal(true);
    setSyncResult(null);
    setError(null);
  };

  const handleConfirmSync = async () => {
    if (!intentToSync) return;

    setSyncLoading(true);
    setError(null);

    try {
      const response = await apiClient.post(
        `/flow-builder/projects/${projectId}/intents/${intentToSync._id}/sync-to-production`
      );

      setSyncResult(response.data);
      console.log('Intent synced successfully:', response.data);
      
      // Auto-close modal after 3 seconds on success
      setTimeout(() => {
        handleCancelSync();
      }, 3000);

    } catch (error) {
      console.error('Error syncing intent:', error);
      setError(error.response?.data?.message || 'Failed to sync intent to production');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCancelSync = () => {
    setShowSyncModal(false);
    setIntentToSync(null);
    setSyncResult(null);
    setError(null);
  };

  // Filter intents based on search term - ensure intents is always an array
  const safeIntents = Array.isArray(intents) ? intents : [];
  const filteredIntents = safeIntents.filter(intent =>
    intent?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    intent?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (intent?.utterances && Array.isArray(intent.utterances) && intent.utterances.some(utterance => 
      utterance?.toLowerCase().includes(searchTerm.toLowerCase())
    ))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Intents</h2>
          <p className="text-gray-600 mt-1">
            Define user intents with sample utterances to trigger flows
          </p>
        </div>
        {canManage && (
          <button
            onClick={handleCreateIntent}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center gap-2"
          >
            <span>➕</span>
            Create Intent
          </button>
        )}
      </div>

      {/* Intent Classification Settings */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 mb-1">Intent Detection Method</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${intentClassificationEnabled ? 'text-indigo-600' : 'text-gray-500'}`}>
                  🤖 LLM-based
                </span>
                
                {/* Toggle Switch */}
                <button
                  onClick={() => handleIntentClassificationToggle(!intentClassificationEnabled)}
                  disabled={updatingSettings}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    intentClassificationEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                  } ${updatingSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      intentClassificationEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                
                <span className={`text-sm font-medium ${!intentClassificationEnabled ? 'text-orange-600' : 'text-gray-500'}`}>
                  🔧 Rule-based
                </span>
              </div>
              
              {updatingSettings && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                  Updating...
                </div>
              )}
            </div>
            
            <p className="text-sm text-gray-600 mt-2">
              {intentClassificationEnabled ? (
                <span>
                  <strong>LLM-based:</strong> Uses AI to understand intent from descriptions and context. More flexible and accurate.
                </span>
              ) : (
                <span>
                  <strong>Rule-based:</strong> Uses exact pattern matching with sample utterances. Faster but requires comprehensive examples.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search intents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
        />
        <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Intents List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredIntents.map((intent) => (
          <div
            key={intent._id}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            {/* Intent Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {intent.name}
                </h3>
                {intent.description && (
                  <p className="text-gray-600 text-sm">
                    {intent.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4">
                {canManage && (
                  <button
                    onClick={() => handleEditIntent(intent)}
                    className="text-indigo-600 hover:text-indigo-800 p-1"
                    title="Edit Intent"
                  >
                    ✏️
                  </button>
                )}
                {shouldShowSyncButton() && canManage && (
                  <button
                    onClick={() => handleSyncToProduction(intent)}
                    className="text-purple-600 hover:text-purple-800 p-1"
                    title="Sync to production"
                  >
                    🚀
                  </button>
                )}
                {canManage && (
                  <button
                    onClick={() => handleDeleteIntent(intent._id)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Delete Intent"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>

            {/* Priority Badge */}
            <div className="mb-4">
              <span className={`
                inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                ${intent.priority >= 50 
                  ? 'bg-red-100 text-red-800' 
                  : intent.priority >= 25 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : 'bg-green-100 text-green-800'
                }
              `}>
                Priority: {intent.priority}
              </span>
            </div>

            {/* Sample Utterances */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Sample Utterances ({intent.utterances.length})
              </h4>
              <div className="space-y-1">
                {intent.utterances.slice(0, 3).map((utterance, index) => (
                  <div
                    key={index}
                    className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded truncate"
                  >
                    "{utterance}"
                  </div>
                ))}
                {intent.utterances.length > 3 && (
                  <div className="text-xs text-gray-500">
                    +{intent.utterances.length - 3} more
                  </div>
                )}
              </div>
            </div>

            {/* Created By */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Created by {intent.createdBy?.name}</span>
                <span>{new Date(intent.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredIntents.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🎯</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No intents found' : 'No intents yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm 
              ? 'Try adjusting your search terms'
              : 'Create your first intent to get started with flow building'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={handleCreateIntent}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
            >
              Create Intent
            </button>
          )}
        </div>
      )}

      {/* Intent Modal */}
      {showModal && (
        <IntentModal
          projectId={projectId}
          intent={selectedIntent}
          onClose={handleModalClose}
          onSave={handleIntentSave}
          canManage={canManage}
          hasReadOnlyAccess={hasReadOnlyAccess}
        />
      )}

      {/* Sync to Production Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {syncResult ? 'Sync Complete' : 'Sync Intent to Production'}
            </h3>
            
            {!syncResult ? (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-yellow-500 text-lg">⚠️</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Important
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>This will sync the intent <strong>"{intentToSync?.name}"</strong> to production.</p>
                        <p className="mt-2">This action will:</p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>Overwrite the production version completely</li>
                          <li>Include all intent configurations and utterances</li>
                          <li>Create a backup for rollback (contact support if needed)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-600">
                  <p><strong>Intent:</strong> {intentToSync?.name}</p>
                  <p><strong>Description:</strong> {intentToSync?.description || 'No description'}</p>
                  <p><strong>Utterances:</strong> {intentToSync?.utterances?.length || 0}</p>
                  <p><strong>Priority:</strong> {intentToSync?.priority}</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancelSync}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={syncLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmSync}
                    disabled={syncLoading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {syncLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Syncing...
                      </>
                    ) : (
                      <>
                        🚀 Sync to Production
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Success Message */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-green-500 text-lg">✅</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        Sync Successful
                      </h3>
                      <div className="mt-2 text-sm text-green-700">
                        <p>{syncResult.message}</p>
                        <div className="mt-2">
                          <p><strong>Intent:</strong> {syncResult.data?.sync?.intentName}</p>
                          <p><strong>Utterances synced:</strong> {syncResult.data?.sync?.utterancesCount}</p>
                          <p><strong>Synced at:</strong> {new Date(syncResult.data?.sync?.syncedAt).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-600">This modal will close automatically in a few seconds.</p>
                  <button
                    onClick={handleCancelSync}
                    className="mt-3 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntentsManager; 